import { fromZonedTime, toZonedTime } from "date-fns-tz";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useHeatmapConfigQuery,
  useHeatmapEventsQuery,
  useOrganizationGeozonesQuery,
  useOrganizationGroupsQuery,
} from "../../api/network/adminApiServices";
import { useLazyGImageryMetaQuery } from "../../api/network/mapApiServices";
import {
  setMapStateData,
  setModalsData,
  TModalsState,
} from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { OrganizationEntityListingPayload } from "../../api/types/Admin";
import {
  LegacyGeozoneEntityListingPayload,
  LegacyGeozoneType,
  TLegacyGeozone,
} from "../../api/types/Geozone";
import {
  LegacyGroupEntityListingPayload,
  TLegacyGroup,
} from "../../api/types/Group";
import {
  THeatmapConfig,
  THeatmapEventsPayload,
  THeatmapEventsResponseSuccess,
  THeatmapEventsResponseTripItemEvent,
  THeatmapLegendDropdownItem,
  THeatmapLegendItem,
  THeatmapTrip,
} from "../../api/types/Heatmap";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../api/types/VehicleStatusCode";
import svgIcons from "../../assets/svg/svgIcons";
import Accordian from "../../components/accordian";
import {
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../components/admin/formFields";
import { TGroupSelectorItem } from "../../components/common/groupSelector.type";
import HierarchyGroupSelector from "../../components/common/hierarchyGroupSelector";
import ModalGroupSelector from "../../components/common/modalGroupSelector";
import BasicMap, { MapLoadingAnimation } from "../../components/maps/basicMap";
import MapTopRightControls from "../../components/maps/mapTopRightControls";
import TooptipInfoButton from "../../components/tooltipInfoButton";
import {
  APP_CONFIG,
  GOOGLE_MAP_MODE_NAMES,
  REPORT_FILTER_BY_OPTION_VALUES,
} from "../../constants/constants";
import { ACL_NAMES, useACL } from "../../hooks/acl";
import { useGMapMode } from "../../hooks/mapGoogle";
import { useMapInit } from "../../hooks/mapInit";
import { useAccountTimezone } from "../../hooks/timezone";
import { routeUrls } from "../../navigation/routeUrls";
import {
  TGeozoneMapDataCircle,
  TGeozoneMapDataPolygon,
  TLatLng,
  TMapLayerOptions,
  TMapState,
  TMapType,
} from "../../types/map";
import { hexToRgb } from "../../utils/common";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  jsTimeToDBTime,
} from "../../utils/dateTime";
import {
  filterOutWhiteGeozones,
  getGeozoneShapeCenter,
} from "../../utils/geozone";
import { useLoggedInUserData } from "../../utils/user";
import { mapOperations as mapOperationsCircle } from "../admin-details/geozone/map-circle";
import { mapOperations as mapOperationsPolygon } from "../admin-details/geozone/map-polygon";
import { mapOperations as mapOperationsRoute } from "../admin-details/geozone/map-route";
import { TMapOperationsProps } from "../admin-details/geozone/type";
import {
  geozonePrepareMapState,
  getGeozoneLocs,
} from "../admin-details/geozone/util";
import { ROLE_ACCESS_LEVELS } from "../admin-details/role/constants";
import { SORTING_OPTION_VALUES } from "./constants";
import { useHeatmapContext } from "./context/heatmap.context";
import {
  mapOperations,
  mapUpdatesHandler,
  updateEntitiesOnMapTypeChange,
} from "./map";
import ModalLayerFilters from "./modalLayerFilters";
import RouteAddress from "./routeAddress";
import { consistentDisplayAssetItem } from "../../utils/vehicle";
import {
  TAssetLocationDataPoint,
  TMapData,
  TMapRef,
  TMapRefGeozones,
  TMemoizedMapOperationsProps,
} from "./type";
import DirectionsPanel from "../../components/maps/DirectionPanel";

const debugComponent = false;

const RouteCheckboxItem = ({
  id,
  label,
  icon,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  icon: string;
  onChange: () => void;
}) => {
  return (
    <label
      className="flex items-center gap-2 cursor-pointer hover:bg-accent-blue-paleO66 rounded-md py-2 px-2"
      htmlFor={id}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="size-4 rounded-sm"
      />
      <span className="flex-grow text-sm leading-4 text-accent-blue-dark">
        {label}
      </span>
      <span className="flex size-6">
        <img src={icon} alt={"line-style"} className="flex" />
      </span>
    </label>
  );
};
// main component
const ScreenMapHeat = () => {
  const { deviceId } = useParams<{ deviceId: any }>();
  const { t } = useTranslation("translation", { keyPrefix: "mapHeat" });
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "dashboard.sidemenu.admins",
  });
  const { t: tMain } = useTranslation();
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  const { getUserACLAccessLevel } = useACL();
  const {
    allGroupsSelected,
    selectedGroups,
    setSelectedGroups,
    setAllGroupsSelected,
    allAssetsSelected,
    selectedAssets,
    setAllAssetsSelected,
    selectedDataType: selectedGroupAssetFilterType,
  } = useHeatmapContext();
  const navigate = useNavigate();
  const [showSortingDropdown, setShowSortingDropdown] = useState(false);
  const [selectedSorting, setSelectedSorting] = useState<string>(
    SORTING_OPTION_VALUES.LATEST_FIRST
  );
  const [searchedKeyword, setSearchedKeyword] = useState<string>("");
  const [showRouteViewsPopup, setShowRouteViewsPopup] = useState(false);
  useEffect(() => {
    setAllGroupsSelected(true);
    setAllAssetsSelected(true);
    // check if heatmap is accessible
    if (
      getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_HEATMAP) <
      ROLE_ACCESS_LEVELS.READ
    ) {
      navigate(routeUrls.dashboardChildren.mapsChildren.fleet);
    }
  }, []);

  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const { gMapGetMode, gMapSetMode } = useGMapMode();
  const dispatch = useDispatch();

  const mapWrapperRef = React.createRef<HTMLDivElement>();

  const accountData = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );

  // check if group hierarchy is enabled
  // Update 8/1/2025 - for heatmap where restriction is applied, as we have specific groups to select from, we should disable the hierarchy selector
  const accountHasGroupHierarchyEnabled: string = "0"; // useLoggedInUserData("groupHierarchy");

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const [allHeatmapTrips, setAllHeatmapTrips] = useState<THeatmapTrip[]>();
  const [allLegendFilters, setAllLegendFilters] = useState<
    THeatmapLegendDropdownItem[]
  >([]);
  const [selectedLegendFilters, setSelectedLegendFilters] = useState<
    THeatmapLegendDropdownItem[]
  >([]);
  const [vehicleListCurrentPage, setVehicleListCurrentPage] = useState(1); // to keep track of current page of vehicle listing (frontend pagination)

  const [legendExpanded, setLegendExpanded] = useState(false);

  // states for hovered trip
  const [hoveredTrip, setHoveredTrip] = useState<number | null>(null);

  // replay heatmap events
  const [replayInProgress, setReplayInProgress] = useState(false);
  const [replayPercentage, setReplayPercentage] = useState<number | null>(null);
  const onHeatmapReplayEvents = () => {
    if (replayInProgress) return;
    setReplayInProgress(true);
    mapUpdatesHandler(getMapOpsProps(), "tripReplayEvents", {
      replayPercChangeCallback: (perc: number | null) => {
        setReplayPercentage(perc);
      },
      completeCallback: () => {
        setReplayPercentage(null);
        setReplayInProgress(false);
      },
    });
  };

  // map init if not already done
  const { initMap, gIdentifyAndSetMapModeToStore } = useMapInit();
  useEffect(() => {
    if (!mapState?.mapScriptLoaded) {
      initMap();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * reset map type on mount
   * -----------------------
   * Before Sep 23 2024, we were trying to set map type to road on unmount,
   * but it was causing issues with mapdata as rest of the map data
   * including mapScriptLoaded was getting erased.
   * This was happening because `mapState` (store data) was unavailable on unmount.
   */
  useEffect(() => {
    dispatch(
      setMapStateData({
        // ...mapState,
        mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
          .type as TMapType,
      })
    );
    return () => {
      // reset everything
      resetPaginationForBatchFetching();
      dispatch(
        setMapStateData({
          // ...mapState,
          mapType: APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
            .type as TMapType,
        })
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // implemented to revert back to last zoomed view when vehicle details modal is closed
  // however, this behaviour is disabled as of 7/2/2024 as it is clashing with other map operations
  const [lastMapView, setLastMapView] = useState<any>(null);

  // handle vehicle selection
  // const selectMapVehicle = useCallback(
  //   (vehicleId: string | null) => {
  //     // re-center map...
  //     if (vehicleId === null) {
  //       // disabled 7/2/2024, nothing happens when vehicle details modal is closed
  //       // if(!!lastMapView && !!lastMapView.center && !!lastMapView.zoom) {
  //       //   // revert to last zoomed view
  //       //   mapUpdatesHandler(
  //       //     getMapOpsProps(),
  //       //     'centerToCoords',
  //       //     {
  //       //       center: [
  //       //         lastMapView.center.latitude,
  //       //         lastMapView.center.longitude,
  //       //       ],
  //       //       zoom: lastMapView.zoom,
  //       //     }
  //       //   );
  //       // } else {
  //       // to all selected vehicles
  //       mapUpdatesHandler(getMapOpsProps(), "centerToPushpin", checkedVehicles);
  //       // }
  //     }
  //     // center map to selected vehicle and update the vehicle icon to focussed state
  //     setSelectedVehicle((prevVehicleId) => {
  //       mapUpdatesHandler(getMapOpsProps(), "focusPushpin", {
  //         id: prevVehicleId ?? selectedVehicle,
  //         focus: false,
  //       });
  //       if (prevVehicleId === vehicleId) {
  //         setTimeout(() => {
  //           dispatch(
  //             setModalsData({
  //               ...modalsState,
  //               showGroupSelector: false,
  //               showHierarchyGroupSelector: false,
  //               showVehicleDetails: false,
  //             })
  //           );
  //         }, 200); // delay prevents a `Cannot update a component` warning
  //         return null;
  //       }
  //       return vehicleId;
  //     });
  //   },
  //   [checkedVehicles, selectedVehicle, modalsState] // eslint-disable-line react-hooks/exhaustive-deps
  // );

  // preparing query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const [orgVehiclesQueryParams, setOrgVehiclesQueryParams] =
    React.useState<OrganizationEntityListingPayload>({
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.VEHICLE_PAGE_SIZE,
      search: "",
      //is_active: "both", // TODO: [P] need to discuss with hemant not in API params
    });

  // fetch groups data
  const {
    data: dataOrgGroups,
    isFetching: isFetchingOrgGroups,
    error: errorOrgGroups,
  } = useOrganizationGroupsQuery(
    {
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
      search: "",
    } as LegacyGroupEntityListingPayload,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [filteredGroupData, setFilteredGroupData] = useState<
    TGroupSelectorItem[]
  >([]);

  // useEffect(() => {
  //   setFilteredGroupData(
  //     (accountHasGroupHierarchyEnabled === "1"
  //       ? []
  //       : (dataOrgGroups?.results?.map((group) => {
  //           return {
  //             groupid: group.groupid,
  //             is_hgroup: false,
  //             checked: true,
  //           };
  //         }) ?? [])) as TGroupSelectorItem[]
  //   );
  // }, [dataOrgGroups]);

  // event data filters
  const { convertToTZForDateFns, tzForDateFns, tzForDisplay, tzReady } =
    useAccountTimezone();
  const [eventDataFilters, setEventDataFilters] =
    useState<THeatmapEventsPayload>({
      end_timestamp: jsTimeToDBTime(
        toZonedTime(new Date(), tzForDateFns).getTime()
      ),
      group_ids: "",
      device_ids: "",
    });
  useEffect(() => {
    if (tzReady) {
      setEventDataFilters((prev) => ({
        ...prev,
        end_timestamp: jsTimeToDBTime(
          toZonedTime(new Date(), tzForDateFns).getTime()
        ),
      }));
    }
  }, [tzReady, tzForDateFns]);

  const { data: dataHeatmapConfig, isFetching: isFetchingHeatmapConfig } =
    useHeatmapConfigQuery();

  const [allApplicableGroups, setAllApplicableGroups] = useState<
    TLegacyGroup[]
  >([]);
  const [groupsSelectionComplete, setGroupsSelectionComplete] =
    useState<boolean>(false);
  useEffect(() => {
    if (
      !!dataOrgGroups &&
      !isFetchingOrgGroups &&
      !!dataHeatmapConfig &&
      !!dataHeatmapConfig.results[0] &&
      !isFetchingHeatmapConfig
    ) {
      const applicableGroupIds = dataHeatmapConfig.results[0].groups.map(
        (_item) => _item.group_id
      );
      setAllApplicableGroups(
        dataOrgGroups.results.filter((groupItem) =>
          applicableGroupIds.includes(groupItem.groupid)
        )
      );
      const allFilters = dataHeatmapConfig?.results?.[0]?.legends?.map(
        (legendItem, i) => {
          const { created_at, updated_at, ...restLegendItem } =
            legendItem as any as THeatmapLegendItem & {
              created_at: any;
              updated_at: any;
            };
          return {
            ...restLegendItem,
            value: `${((legendItem as any).id || 0) as number}`,
            label:
              i === dataHeatmapConfig?.results?.[0]?.legends.length - 1
                ? t("legend_over_n_hours", {
                    n: legendItem.start_hour,
                  })
                : t("legend_from_to_hours", {
                    from: legendItem.start_hour,
                    to: legendItem.end_hour,
                  }),
          };
        }
      );
      setAllLegendFilters(allFilters);
      setSelectedLegendFilters(allFilters);
    }
  }, [
    dataOrgGroups,
    isFetchingOrgGroups,
    dataHeatmapConfig,
    isFetchingHeatmapConfig,
  ]);

  // update filteredGroupData
  useEffect(() => {
    setFilteredGroupData(
      (accountHasGroupHierarchyEnabled === "1"
        ? []
        : (allApplicableGroups?.map((group) => {
            return {
              groupid: group.groupid,
              description: group.description,
              is_hgroup: false,
              checked: true,
            };
          }) ?? [])) as TGroupSelectorItem[]
    );
  }, [allApplicableGroups]);

  // fetch vehicles data with new single API call (with filters)
  const {
    data: dataHeatmapEvents,
    isFetching: isFetchingHeatmapEvents,
    error: errorHeatmapEvents,
  } = useHeatmapEventsQuery(
    {
      ...(!!eventDataFilters.end_timestamp
        ? { end_timestamp: eventDataFilters.end_timestamp }
        : { end_timestamp: 0 }),
      ...(selectedGroups.length > 0 &&
      !allGroupsSelected &&
      selectedGroupAssetFilterType === REPORT_FILTER_BY_OPTION_VALUES.GROUP
        ? {
            group_ids: selectedGroups.map((group: string) => group).join(","),
          }
        : { group_ids: "" }),
      ...(selectedAssets.length > 0 &&
      !allAssetsSelected &&
      selectedGroupAssetFilterType === REPORT_FILTER_BY_OPTION_VALUES.ASSET
        ? {
            device_ids: selectedAssets.map((asset: string) => asset).join(","),
          }
        : { device_ids: "" }),
    },
    {
      skip:
        !mapState?.mapScriptLoaded ||
        !eventDataFilters.end_timestamp ||
        eventDataFilters.end_timestamp === 0 ||
        isFetchingHeatmapConfig ||
        isFetchingOrgGroups ||
        !dataHeatmapConfig ||
        dataHeatmapConfig.results.length === 0 ||
        allApplicableGroups.length === 0 ||
        !groupsSelectionComplete ||
        allLegendFilters.length === 0,
      refetchOnMountOrArgChange: true,
    }
  );

  // update allHeatmapTrips with fetched data
  useEffect(() => {
    if (!!dataHeatmapEvents && !isFetchingHeatmapEvents) {
      const newAllHeatmapTrips = Object.keys(
        dataHeatmapEvents as THeatmapEventsResponseSuccess
      )
        .reduce((acc, curr: string, currIndex) => {
          const tripItem = (
            dataHeatmapEvents as THeatmapEventsResponseSuccess
          )?.[curr as keyof THeatmapEventsResponseSuccess];
          return [
            ...acc,
            {
              trip: curr,
              tripIndex: currIndex + 1,
              ...tripItem,
              events: tripItem.events.map((eventItem, eventIndex) => ({
                ...eventItem,
                // added this unique numeric recordid to uniquely identify events
                recordid: 99e15 + (currIndex + 1) * 1e12 + eventIndex,
              })),
            } as THeatmapTrip,
          ];
        }, [] as THeatmapTrip[])
        .filter((heatmapTrip, i) => {
          const hoursDifference =
            Math.abs(
              eventDataFilters.end_timestamp - heatmapTrip.end_timestamp
            ) /
            (60 * 60);
          // allLegendFilters.forEach((legendItem, i) => {
          //   if (
          //     // find the correct legend item based on hours difference for comparison
          //     hoursDifference >= legendItem.start_hour &&
          //     hoursDifference < legendItem.end_hour &&
          //     // ensure that the legend item is selected
          //     selectedLegendFilters.some(
          //       (selectedLegendItem) =>
          //         selectedLegendItem.value === legendItem.value
          //     )
          //   )
          //     return true;
          // });
          // filter out trips that do not match any selected legend filter
          return selectedLegendFilters.some((selectedLegendItem) => {
            const legendItem = allLegendFilters.find(
              (item) => item.value === selectedLegendItem.value
            );
            if (
              legendItem &&
              hoursDifference >= legendItem.start_hour &&
              hoursDifference < legendItem.end_hour
            ) {
              return true;
            }
            // if no legend item matches, filter out the trip
            if (i === 0 && APP_CONFIG.DEBUG.MAPS)
              console.warn(
                `Trip ${heatmapTrip.trip} does not match any selected legend filter`
              );
            // return false to filter out the trip
            return false;
          });
        });
      setAllHeatmapTrips(newAllHeatmapTrips);
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("allHeatmapTrips", newAllHeatmapTrips);
    }
  }, [
    dataHeatmapEvents,
    isFetchingHeatmapEvents,
    eventDataFilters,
    allLegendFilters,
    selectedLegendFilters,
  ]);

  const resetPaginationForBatchFetching = () => {
    setVehicleListCurrentPage(1);
    setAllHeatmapTrips(undefined);
    // vehiclesBatch.current = 1;
    setOrgVehiclesQueryParams({ ...orgVehiclesQueryParams, page: 1 });
    // selectMapVehicle(null);
  };

  const handleGroupFilterChange = (groups: TGroupSelectorItem[]) => {
    resetPaginationForBatchFetching();
    setFilteredGroupData(groups);
  };

  // const [dataPoints, setDataPoints] = useState<TAssetLocationDataPoint[]>([]);

  // helper function to get map operations props
  const getMapOpsProps: TMemoizedMapOperationsProps = useCallback(() => {
    return {
      mapRef,
      mapState,
      mapData: { ...mapState?.mapData } as TMapData,
      mapLayerOptions: mapState?.mapLayerOptions as TMapLayerOptions,
      setMapData: () => {},
      // TODO - DISABLED TEMPORARILY ============== FIX ISSUE HERE
      dataPoints: allHeatmapTrips || [], // dataPoints,
      selectedSorting: "desc",
      selectedTripIndex: null,
      // selectedTripIndex:
      // selectedTrip === null
      //   ? null
      //   : selectedSorting === "desc"
      //     ? sanitizedVehicleMapData.length - 1 - selectedTrip
      //     : selectedTrip,
      selectedStatusCodes: [],
      // selectedStatusCodes: filteredStatusCodes,
      onViewChangeEnd: (center, zoom) => {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log("[onViewChangeEnd] center:", center, "zoom:", zoom);
        dispatch(
          setMapStateData({
            // ...mapState,
            mapCenter: center,
            mapZoom: zoom,
            mapData: {
              ...mapState.mapData,
              centerPosition: center,
            },
            mapType:
              gMapGetMode(mapRef.current.map) ===
              GOOGLE_MAP_MODE_NAMES.STREETVIEW
                ? (APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
                    .type as TMapType)
                : mapRef.current.map?.getMapTypeId() ===
                    APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE]
                      .id
                  ? (APP_CONFIG.MAPS.TYPES[
                      APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE
                    ].type as TMapType)
                  : mapState.mapType,
          })
        );
        // this function gets triggered on closing streetside view
        // make map entities visible here
        if (
          mapState?.mapType ===
            APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].type &&
          mapRef.current.map?.getMapTypeId() ===
            APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].id
        ) {
          if (APP_CONFIG.DEBUG.MAPS)
            console.log(
              `Attempt to switch to road view (when closing streetside), params: ${mapState?.mapType}`
            );
        }
        // if(APP_CONFIG.DEBUG.MAPS) console.log('Switching to road view after streetside view is closed')
        // handleMapTypeChange('road')
      },
      onMapTypeChange: (event) => {
        if (APP_CONFIG.DEBUG.MAPS) console.log("[onMapTypeChange]", event);
        // with google maps, store value updates automatically via `tilesloaded` event, so
        // we just update the map mode using Google Maps JS SDK using `gMapSetMode` in
        // `handleMapTypeChange` below
        // -------------------------------------------------------------------------------
        // dispatch(
        //   setMapStateData({
        //     ...mapState,
        //     mapType:
        //       (APP_CONFIG.MAPS.TYPES.find(
        //         (item) => item.id === event.newMapTypeId
        //       )?.type as TMapType) ?? "",
        //   })
        // );
      },
      saveLastView: (center: any, zoom: number) => {
        if (APP_CONFIG.DEBUG.MAPS) console.log("[saveLastView]", center, zoom);
        setLastMapView({ center, zoom });
      },
      heatmapParams: {
        heatmapConfig:
          dataHeatmapConfig?.results?.[0] || ({} as THeatmapConfig),
        timestamp: eventDataFilters.end_timestamp,
      },
    };
  }, [
    mapState,
    allHeatmapTrips,
    dataHeatmapConfig,
    eventDataFilters.end_timestamp,
  ]);

  // show address searchbox
  const [showAddressSearchbox, _] = useState(false);

  // common map ref to be used for various map operations
  const mapRef = React.useRef<TMapRef>({
    map: null,
    objects: {
      mTripLayers: [],
      mDirectionsManagers: [],
      mTripsVisibility: [],
      mWaypoints: [],
      mPushpins: [],
      mClusterLayer: null,
      mTripsData: [],
    },
    routeViewsEnabled: {
      DEFAULT: true,
      POINT_TO_POINT: false,
      SNAP_TO_ROAD: false,
      DIRECTIONS_SERVICE: false,
    },
  });

  // fetch geozones data
  const { data: dataOrgGeozones, isFetching: isFetchingOrgGeozones } =
    useOrganizationGeozonesQuery(
      {
        accountid: `${thisUserOrganizationId}`,
        page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
        page: 1,
      } as LegacyGeozoneEntityListingPayload,
      {
        skip: !mapState?.mapScriptLoaded,
      } // || mapState?.mapLayerOptions?.hide_geozones
    );

  // common map ref for geozone map operations
  const mapRefGeozones = React.useRef<TMapRefGeozones>({});

  const renderGeozones = () => {
    if (mapState?.mapLayerOptions?.hide_geozones) return;
    if (
      !!dataOrgGeozones &&
      !isFetchingOrgGeozones &&
      mapState?.mapData?.ready
    ) {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("[handleMapReady] [renderGeozones]");
      dataOrgGeozones?.results
        ?.filter(filterOutWhiteGeozones)
        ?.forEach((geozone) => {
          // initialize map ref for each geozone
          const thisGeozoneMapRef = (mapRefGeozones.current[geozone.geozoneid] =
            {
              current: {
                map: mapRef.current.map,
                objects: {},
              },
            });
          // prepare map state for each geozone
          const geozoneLocs = getGeozoneLocs(
            geozone as Partial<TLegacyGeozone>
          );
          const geozoneCenter = getGeozoneShapeCenter(
            geozone as Partial<TLegacyGeozone>,
            "object"
          ) as TLatLng;
          const mapDataFromSavedRecord = {
            //values.properties as TGeozoneMapData;
            centerPosition: geozoneCenter,
            locs: geozoneLocs,
            radius: geozone.radius,
          };
          const newMapState: TMapState = geozonePrepareMapState({
            seedMapState: mapState,
            shapeType: geozone.zonetype,
            seedMapData: mapDataFromSavedRecord,
            centerPosition:
              mapDataFromSavedRecord?.centerPosition ??
              APP_CONFIG.MAPS.DEFAULTS.CENTER,
            polygonSides:
              (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs
                ?.length ?? APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
            locs:
              (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs ??
              undefined,
            radius:
              (mapDataFromSavedRecord as TGeozoneMapDataCircle)?.radius ??
              APP_CONFIG.MAPS.DEFAULTS.RADIUS,
            color: hexToRgb(`${geozone.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
            editable: false,
            ready: true,
          });
          const mapOperationsParams = {
            mapRef: thisGeozoneMapRef, // mapRefGeozones.current[geozone.geozoneid],
            mapData: newMapState.mapData || mapDataFromSavedRecord,
            setMapData: () => {},
            drawShapeOnly: true,
            geozoneId:
              loggedInUserData?.user?.enablegeozonelabels === 1
                ? geozone.geozoneid
                : undefined,
          } as TMapOperationsProps;
          // check zone type and call respective map operations
          if (geozone.zonetype === null || isNaN(geozone.zonetype)) return; // prevents error when zonetype is null due to bad API data
          switch (parseInt(geozone?.zonetype?.toString())) {
            case LegacyGeozoneType.CIRCLE:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[CIRCLE]");
              mapOperationsCircle(mapOperationsParams);
              break;
            case LegacyGeozoneType.POLYGON:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[POLYGON]");
              mapOperationsPolygon(mapOperationsParams);
              break;
            case LegacyGeozoneType.ROUTE:
              if (APP_CONFIG.DEBUG.GEOZONES)
                console.log("geozoneid:", geozone.geozoneid, "[ROUTE]");
              mapOperationsRoute(mapOperationsParams);
              break;
            default:
              break;
          }
        });
    } else {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log(
          "[handleMapReady] [renderGeozones] ELSE ",
          !!dataOrgGeozones,
          !isFetchingOrgGeozones,
          mapState?.mapData?.ready
        );
    }
  };

  const [mapSearchboxOpen, setMapSearchboxOpen] = useState(false);
  const [directionsRenderer, setDirectionsRenderer] = React.useState(null);

  const handleDirectionsChange = (renderer: any) => {
    setDirectionsRenderer(renderer);
  };

  // used for any map state transition to refresh map entities
  const [mapStateTransitionInProgress, setMapStateTransitionInProgress] =
    useState(false);

  const loadResetMapDataWithInitialValues = () => {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("[loadResetMapDataWithInitialValues]");
    setMapStateTransitionInProgress(true);
    const newMapState: Partial<TMapState> = {
      // ...mapState,
      mapCenter: APP_CONFIG.MAPS.DEFAULTS.CENTER,
      mapData: {
        centerPosition: APP_CONFIG.MAPS.DEFAULTS.CENTER,
        ready: true,
      },
    };
    if (APP_CONFIG.DEBUG.MAPS) console.log("newMapState", newMapState);
    setTimeout(() => {
      dispatch(setMapStateData(newMapState));
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  // set map data on page load
  useEffect(() => {
    if (
      !!allHeatmapTrips &&
      // && !isFetchingOrgVehicles
      // && !!allHeatmapTripsEvents
      !isFetchingHeatmapEvents
    ) {
      console.log("/// allHeatmapTrips", allHeatmapTrips);
      dispatch(setModalsData({ ...modalsState, showVehicleFilter: false }));
      // setDataPoints(
      //   allHeatmapTrips
      //     // convert THeatmapTrip[] to AssetLocationDetails[]
      //     ?.reduce((acc, curr) => {
      //       return [
      //         ...acc,
      //         // TODO - DISABLED TEMPORARILY ============== FIX ISSUE HERE
      //         {
      //           accountid: curr.events?.[0]?.accountid,
      //             asset_map_status: ASSET_MAP_STATUS.DRIVING,
      //             description: curr.deviceid,
      //             deviceid: curr.deviceid,
      //             displayname: curr.deviceid,
      //             equipmenttype: "",
      //             eventdata: {
      //               heading: number;
      //               latitude: number;
      //               longitude: number;
      //               statuscode: number;
      //               timestamp: number;
      //             };
      //             isactive: number;
      //             licenseplate: string;
      //             uniqueid: string;
      //             vehicleid: string;
      //             vehiclemake: string;
      //             vehiclemodel: string;
      //         } as AssetLocationDetails
      //       ];
      //     }, [] as AssetLocationDetails[])
      //   // ?.map((vehicleItem, _) => ({
      //   //   ...vehicleItem,
      //   //   eventData: allHeatmapTripsEvents?.find((eventItem) => eventItem.deviceid === vehicleItem.deviceid),
      //   // }))
      // );
      // setCheckedVehicles(
      //   allHeatmapTrips?.map((item) => getVehicleUUID(item)) ?? []
      // );
      loadResetMapDataWithInitialValues();
    }
  }, [allHeatmapTrips, isFetchingHeatmapEvents]);

  // carry out map operations on map ready
  const handleMapReady = useCallback(() => {
    if (APP_CONFIG.DEBUG.MAPS) console.log("[handleMapReady]");
    if (!allHeatmapTrips) return;
    mapOperations(
      {
        ...getMapOpsProps(),
        ...(errorHeatmapEvents ? { dataPoints: [] } : {}),
        onDataPointPushpinClick: (
          dataPoint: THeatmapEventsResponseTripItemEvent
        ) => {
          console.log("onDataPointPushpinClick", dataPoint);
          // selectMapVehicle(
          //   selectedVehicle === getVehicleUUID(dataPoint)
          //     ? null
          //     : getVehicleUUID(dataPoint)
          // );
        },
      },
      allHeatmapTrips
    );
    renderGeozones();
  }, [
    mapState,
    // dataPoints,
    // checkedVehicles,
    // selectedVehicle,
    mapState?.mapLayerOptions?.hide_geozones,
    dataOrgGeozones, // adding this dependency ensures that updates to geozones are reflected
    allHeatmapTrips,
  ]); // instead of [mapState?.mapData]

  // trigger map state transition on hovered trip change
  useEffect(() => {
    if (!allHeatmapTrips) return;
    mapUpdatesHandler(
      getMapOpsProps(),
      "setTripHoverState",
      selectedSorting === "desc"
        ? hoveredTrip === null
          ? null
          : allHeatmapTrips?.length - 1 - hoveredTrip
        : hoveredTrip,
      // additional data
      { routeViewsEnabled: mapRef.current.routeViewsEnabled }
    );
  }, [hoveredTrip, allHeatmapTrips]);

  // const handleChangeCheckAllVehicles = (
  //   e: React.ChangeEvent<HTMLInputElement>
  // ) => {
  //   if (e.target.checked) {
  //     setCheckedVehicles(dataPoints.map((item) => getVehicleUUID(item)));
  //   } else {
  //     setCheckedVehicles([]);
  //   }
  // };

  // const handleClickCheckbox = (dataPoint: AssetLocationDetails) => {
  //   const vehicleUUID = getVehicleUUID(dataPoint);
  //   if (checkedVehicles.includes(vehicleUUID)) {
  //     if (APP_CONFIG.DEBUG.MAPS) console.log("uncheck", vehicleUUID);
  //     setCheckedVehicles(
  //       checkedVehicles.filter((id: any) => id !== vehicleUUID)
  //     );
  //     // hide vehicle details popup if unchecked
  //     dispatch(
  //       setModalsData({
  //         ...modalsState,
  //         showGroupSelector: false,
  //         showHierarchyGroupSelector: false,
  //         showLayerFilter: false,
  //         showVehicleFilter: false,
  //         showVehicleDetails: false,
  //       })
  //     );
  //   } else {
  //     if (APP_CONFIG.DEBUG.MAPS) console.log("check", vehicleUUID);
  //     setCheckedVehicles([...checkedVehicles, vehicleUUID]);
  //   }
  // };

  const [gImageryMetaApiTrigger, { isFetching: isFetchingGImageryMeta }] =
    useLazyGImageryMetaQuery();

  const handleMapTypeChange = (
    mapType: TMapType,
    latLng?: google.maps.LatLng
  ) => {
    if (APP_CONFIG.DEBUG.MAPS) console.log("[handleMapTypeChange]", mapType);

    const successCallback = () => {
      gMapSetMode(
        mapRef.current.map,
        mapType as TMapType,
        latLng || mapRef.current.map.getCenter(),
        {
          // callback checks if streetside mode is enabled and sets applicable map type in store
          onStreetViewVisibleChanged: (panoVisible: boolean) => {
            if (APP_CONFIG.DEBUG.MAPS)
              console.log(
                "[onStreetViewVisibleChanged] attached at map-heat.tsx"
              );
            const newMapMode = gIdentifyAndSetMapModeToStore(
              mapRef.current.map
            );
            if (APP_CONFIG.DEBUG.MAPS)
              console.log("SVCHANGED newMapMode:", newMapMode);
            const panorama = mapRef.current.map.getStreetView();
            setTimeout(() => {
              if (!panorama.getVisible()) {
                // exited streetview
                const newMapMode = gIdentifyAndSetMapModeToStore(
                  mapRef.current.map
                );
                if (APP_CONFIG.DEBUG.MAPS)
                  console.log(
                    "SVCHANGED - pano not visible (exited) newMapMode:",
                    newMapMode
                  );
                // update user map activity in store
                // if (!recordActivityAsPage || refreshingRef.current) return;
                // if (userMapRefreshing) {
                //   if (APP_CONFIG.DEBUG.MAPS)
                //     console.log(
                //       "SVCHANGED - NO - userMapRefreshing",
                //       userMapRefreshing,
                //       "RETURNING"
                //     );
                //   return;
                // }
                if (APP_CONFIG.DEBUG.MAPS)
                  console.log(
                    "SVCHANGED - about to trigger usermapactivity change",
                    newMapMode
                  );
                // check if streetview exit was not successful, trigger change to roadmap
                if (newMapMode === "streetview") {
                  if (APP_CONFIG.DEBUG.MAPS)
                    console.log(
                      "SVCHANGED - fix incorrect 'newMapMode' value from 'streetview' to ",
                      GOOGLE_MAP_MODE_NAMES.ROADMAP
                    );
                  gMapSetMode(
                    mapRef.current.map,
                    GOOGLE_MAP_MODE_NAMES.ROADMAP,
                    latLng || mapRef.current.map.getCenter()
                  );
                }
                setTimeout(() => {
                  const newMapMode = gIdentifyAndSetMapModeToStore(
                    mapRef.current.map
                  );
                  if (APP_CONFIG.DEBUG.MAPS)
                    console.log(
                      "SVCHANGED - about to trigger setUserMapActivity change - newMapMode:",
                      newMapMode
                    );
                  // dispatch(
                  //   setUserMapActivity({
                  //     ...userMapActivity,
                  //     assetMap: {
                  //       // ...activityRef.current?.fleetMap,
                  //       ...userMapActivity?.assetMap,
                  //       center: {
                  //         latitude: mapRef.current.map.getCenter().lat(),
                  //         longitude: mapRef.current.map.getCenter().lng(),
                  //       } as TLatLng,
                  //       zoom: mapRef.current.map.getZoom(),
                  //       mapType: newMapMode,
                  //     },
                  //   } as TUserMapActivity)
                  // );
                }, 600);
              } else {
                if (APP_CONFIG.DEBUG.MAPS)
                  console.log("SVCHANGED - pano is visible");
              }
            }, 600);
          },
        }
      );
    };
    // Handle street view
    if (APP_CONFIG.MAPS.VIEW_TYPES_REQUIRING_METADATA_CHECK.includes(mapType)) {
      const center: google.maps.LatLng = mapRef.current.map.getCenter();
      if (
        typeof center.lat() !== "number" ||
        typeof center.lng() !== "number"
      ) {
        if (APP_CONFIG.DEBUG.MAPS) console.error("Map center coords not found");
        toast.error(tMain("maps.type_not_supported"));
        return;
      }
      gImageryMetaApiTrigger({ lat: center.lat(), lng: center.lng() })
        .unwrap()
        .then((response) => {
          if (APP_CONFIG.DEBUG.MAPS)
            console.log("imageryMetaApiTrigger response", response);
          if ((response?.status as any) === "OK") {
            successCallback();
          } else {
            toast.error(tMain("maps.type_not_supported"));
          }
        })
        .catch((error) => {
          toast.error(tMain("maps.type_not_supported"));
        });
    } else {
      successCallback();
    }
    return; // disabling the bing way below
    // setMapStateTransitionInProgress(true);
    const metaDataSuccessCallback = (coords?: TLatLng, zoom?: number) => {
      const Microsoft = (window as any).Microsoft;
      try {
        const zoomCompleteCallback = () => {
          mapRef.current.map.setView({
            ...(!!coords
              ? {
                  center: new Microsoft.Maps.Location(
                    coords.latitude,
                    coords.longitude
                  ),
                }
              : {}),
            mapTypeId: Microsoft.Maps.MapTypeId[mapType],
          });
          dispatch(
            setMapStateData({
              // ...mapState,
              mapType:
                mapRef.current.map?.getMode()?.mapModeType ===
                APP_CONFIG.MAPS.STREETSIDE_MAP_MODE
                  ? (APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
                      .type as TMapType)
                  : mapType,
            })
          );
          updateEntitiesOnMapTypeChange(getMapOpsProps(), mapType);
        };
        if (!!zoom && mapRef.current.map.getZoom() < zoom) {
          mapRef.current.map.setView({
            ...(!!zoom ? { zoom } : {}),
            animate: true,
          });
          setTimeout(zoomCompleteCallback, 500);
        } else {
          zoomCompleteCallback();
        }
      } catch (error) {
        toast.error(tMain("maps.type_change_failed"));
        console.error("Error setting map type", error);
      }
    };
    // if (APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(mapType)) {
    //   const center: TLatLng = mapRef.current.map.getCenter();
    //   console.log("center", center);
    //   imageryMetaApiTrigger({
    //     ...center,
    //     imagerySet:
    //       mapType ===
    //       APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE].type
    //         ? "Birdseye"
    //         : "Streetside",
    //   } as BingImageryMetaRequest)
    //     .then((response) => {
    //       if (APP_CONFIG.DEBUG.MAPS)
    //         console.log("imageryMetaApiTrigger response", response);
    //       if (
    //         !!response.data?.resourceSets?.[0]?.estimatedTotal &&
    //         response.data?.resourceSets?.[0]?.estimatedTotal > 0
    //       ) {
    //         successCallback(
    //           {
    //             latitude:
    //               response.data?.resourceSets?.[0]?.resources?.[0]?.lat ||
    //               center.latitude,
    //             longitude:
    //               response.data?.resourceSets?.[0]?.resources?.[0]?.lon ||
    //               center.longitude,
    //           },
    //           mapType ===
    //             APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE]
    //               .type
    //             ? response.data?.resourceSets?.[0]?.resources?.[0]?.zoomMin
    //             : undefined
    //         );
    //       } else {
    //         toast.error(tMain("maps.type_not_supported"));
    //       }
    //     })
    //     .catch((error) => {
    //       toast.error(tMain("maps.type_not_supported"));
    //     });
    // } else {
    //   successCallback();
    // }
  };

  const handleMapLayerChange = (mapLayerOptions: TMapLayerOptions) => {
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      dispatch(
        setMapStateData({
          // ...mapState,
          mapLayerOptions,
        })
      );
      setMapStateTransitionInProgress(false);
    }, 100);
  };

  const handleModalCloseClick = () => {
    console.log("[handleModalCloseClick]");
    // Reset map to its default state
    // mapUpdatesHandler(getMapOpsProps(), "checkedUpdated", checkedVehicles);
  };

  const onGroupSelectionChange = (selectedGroups: string[]) => {
    // if selection is different from filteredGroupData, then reset the filter
    const divergence1 = filteredGroupData
      .filter((group) => group.is_hgroup && group.checked)
      .filter((group) => !selectedGroups.includes(group.groupid));
    const divergence2 = selectedGroups.filter(
      (group) =>
        !filteredGroupData
          .filter((group) => group.is_hgroup && group.checked)
          .map((group) => group.groupid)
          .includes(group)
    );
    if (APP_CONFIG.DEBUG.ALL) console.log("divergence1", divergence1);
    if (APP_CONFIG.DEBUG.ALL) console.log("divergence2", divergence2);
    if (APP_CONFIG.DEBUG.ALL) console.log("selectedGroups", selectedGroups);
    if (divergence1.length > 0 || divergence2.length > 0) {
      handleGroupFilterChange(
        selectedGroups.map((group) => {
          return {
            groupid: group,
            checked: true,
            is_hgroup: true,
          };
        }) as TGroupSelectorItem[] // used `any` to avoid type mismatch as a workaround
      );
    }
  };

  // set selected groups
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!allApplicableGroups && !isFetchingOrgGroups) {
      setTimeout(() => {
        setGroupsSelectionComplete(true);
      }, 200);
      setSelectedGroups(
        allApplicableGroups?.map((group) => {
          return group.groupid;
        }) ?? ([] as string[])
      );
    }
  }, [allApplicableGroups, isFetchingOrgGroups]);
  // update all groups selected state
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    // disabled as this logic cannot be used to identify if all groups are selected
    // as there could be groups on more pages that are not yet loaded
    // if (!!allApplicableGroups && !isFetchingOrgGroups) {
    //   setAllGroupsSelected(
    //     allApplicableGroups?.length === selectedGroups?.length
    //   );
    // }
  }, [selectedGroups, allApplicableGroups, isFetchingOrgGroups]);

  const MemoizedGroupSelector = useMemo(() => {
    if (!dataOrgGroups) return null;
    return (
      <>
        {accountHasGroupHierarchyEnabled === "1" && (
          <>
            {modalsState.showHierarchyGroupSelector && (
              <HierarchyGroupSelector
                initiallySelectedGroups={filteredGroupData
                  .filter((group) => group.is_hgroup && group.checked)
                  .map((group) => group.groupid)}
                onSelectionChange={(selectedGroups) => {
                  onGroupSelectionChange(selectedGroups);
                }}
              />
            )}
          </>
        )}
        {/* : ( */}
        <ModalGroupSelector
          hideGroupAssetToggle={false}
          allAssetsOptionEnabled={true}
          allGroups={allApplicableGroups ?? []}
          allGroupsOptionEnabled={true}
          filteredGroupData={filteredGroupData}
          // setFilteredGroupData={(e) => handleGroupFilterChange(e)}
          // 01/28/2025 commented above code and added the below code to fix the issue of API not triggering on group selection change
          // filteredGroupData={
          //   !!allApplicableGroups && !isFetchingOrgGroups
          //     ? (allApplicableGroups?.map((group) => ({
          //         ...group,
          //         is_hgroup: false,
          //         checked: (selectedGroups as string[]).includes(
          //           group.groupid
          //         ),
          //       })) as TGroupSelectorItem[])
          //     : []
          // }
          restrictAssetsToThoseUnderAllGroupItems={true}
          setFilteredGroupData={(e: TGroupSelectorItem[]) => {
            if (APP_CONFIG.DEBUG.ALL) console.log("setFilteredGroupData", e);
            return setSelectedGroups(
              e?.filter((group) => group.checked)?.map((group) => group.groupid)
            );
          }}
          key={`group-selector-${isFetchingOrgGroups.toString()}`}
          whichContext="heatmap"
        />
        {/* )} */}
      </>
    );
  }, [
    modalsState.showHierarchyGroupSelector,
    accountHasGroupHierarchyEnabled,
    dataOrgGroups,
    filteredGroupData,
    allApplicableGroups,
  ]);

  // search keyword filter
  const [listableDataPoints, setListableDataPoints] = useState<
    TAssetLocationDataPoint[]
  >([]);
  // useEffect(() => {
  //   setListableDataPoints(
  //     dataPoints
  //       // filter vehicles based on search keyword
  //       ?.filter((dataPoint) => {
  //         const title = dataPoint.deviceid; // mapVehicleDisplayTitle(dataPoint);
  //         const bottomText = `${dataPoint.equipmenttype || ""}`; // dataPoint.licenseplate || dataPoint.vehicleid;
  //         const description = mapVehicleMakeModel(dataPoint); // dataPoint.displayname || dataPoint.vehicleid; // dataPoint.equipmenttype
  //         // search by title, bottomText, description
  //         return (
  //           title?.toLowerCase().includes(searchedKeyword.toLowerCase()) ||
  //           bottomText?.toLowerCase().includes(searchedKeyword.toLowerCase()) ||
  //           description?.toLowerCase().includes(searchedKeyword.toLowerCase())
  //         );
  //       })
  //   );
  // }, [dataPoints, searchedKeyword]);

  // update map data on searched vehicles change
  // useEffect(() => {
  //   if (APP_CONFIG.DEBUG.MAPS) console.log("searched keyword changed");
  //   mapUpdatesHandler(
  //     getMapOpsProps(),
  //     "checkedUpdated",
  //     listableDataPoints.map((item) => getVehicleUUID(item))
  //   );
  // }, [searchedKeyword, listableDataPoints]);

  // hide streetview when user clicks anywhere outside map
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        mapState.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW &&
        mapWrapperRef.current &&
        !mapWrapperRef.current.contains(event.target)
      ) {
        gMapSetMode(
          mapRef.current.map,
          mapRef.current.map?.lastMapModeBeforeStreetview ||
            GOOGLE_MAP_MODE_NAMES.ROADMAP,
          mapRef.current.map.getCenter()
          // { skipEventListenerRegistration: true }
        );
      }
    };

    if (mapState?.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mapState?.mapType, mapWrapperRef, mapRef]);

  // route views checkboxes
  const [routeViewsVersion, bumpRouteViewsVersion] = useState(0);
  // if any of POINT_TO_POINT, SNAP_TO_ROAD or DIRECTIONS_SERVICE route views
  // are enabled, then disable the DEFAULT, and when none of the prior is enabled,
  // then enable the DEFAULT back
  const updateRouteViewsEnabled = useCallback(
    (
      updater: (
        current: typeof mapRef.current.routeViewsEnabled
      ) => typeof mapRef.current.routeViewsEnabled
    ) => {
      const previousRouteViewsEnabled = {
        ...mapRef.current.routeViewsEnabled,
      };
      const nextRouteViewsEnabled = updater({
        ...previousRouteViewsEnabled,
      });
      const defaultWasJustEnabled =
        !previousRouteViewsEnabled.DEFAULT && nextRouteViewsEnabled.DEFAULT;
      const hasAlternateRouteViewEnabled =
        nextRouteViewsEnabled.POINT_TO_POINT ||
        nextRouteViewsEnabled.SNAP_TO_ROAD ||
        nextRouteViewsEnabled.DIRECTIONS_SERVICE;

      if (defaultWasJustEnabled) {
        mapRef.current.routeViewsEnabled = {
          ...nextRouteViewsEnabled,
          DEFAULT: true,
          POINT_TO_POINT: false,
          SNAP_TO_ROAD: false,
          DIRECTIONS_SERVICE: false,
        };
        bumpRouteViewsVersion((x) => x + 1);
        return;
      }

      mapRef.current.routeViewsEnabled = {
        ...nextRouteViewsEnabled,
        DEFAULT: hasAlternateRouteViewEnabled
          ? false
          : nextRouteViewsEnabled.DEFAULT || true,
      };
      bumpRouteViewsVersion((x) => x + 1);
    },
    []
  );
  const RouteCheckboxes = useMemo(() => {
    return (
      <div className="flex flex-col gap-2">
        <RouteCheckboxItem
          id="default"
          label={t("line_styles.default")}
          icon={svgIcons.LineStyleIconDefault}
          checked={mapRef.current?.routeViewsEnabled?.DEFAULT}
          onChange={() => {
            updateRouteViewsEnabled((current) => ({
              ...current,
              DEFAULT: !current.DEFAULT,
            }));
          }}
        />
        <div className="flex h-1 border-t border-t-accent-blue-pale" />
        <RouteCheckboxItem
          id="point-to-point"
          label={t("line_styles.point_to_point")}
          icon={svgIcons.LineStyleIconP2P}
          checked={mapRef.current?.routeViewsEnabled?.POINT_TO_POINT}
          onChange={() => {
            updateRouteViewsEnabled((current) => ({
              ...current,
              POINT_TO_POINT: !current.POINT_TO_POINT,
            }));
          }}
        />
        <RouteCheckboxItem
          id="snap-to-road"
          label={t("line_styles.snap_to_roads")}
          icon={svgIcons.LineStyleIconSnap}
          checked={mapRef.current?.routeViewsEnabled?.SNAP_TO_ROAD}
          onChange={() => {
            updateRouteViewsEnabled((current) => ({
              ...current,
              SNAP_TO_ROAD: !current.SNAP_TO_ROAD,
            }));
          }}
        />
        <RouteCheckboxItem
          id="directions-service"
          label={t("line_styles.directions_service")}
          icon={svgIcons.LineStyleIconDirSvc}
          checked={mapRef.current?.routeViewsEnabled?.DIRECTIONS_SERVICE}
          onChange={() => {
            updateRouteViewsEnabled((current) => ({
              ...current,
              DIRECTIONS_SERVICE: !current.DIRECTIONS_SERVICE,
            }));
          }}
        />
      </div>
    );
  }, [routeViewsVersion, updateRouteViewsEnabled]);

  // trigger map updates on route view checkbox change
  useEffect(() => {
    mapUpdatesHandler(
      getMapOpsProps(),
      "routeViewsChange",
      mapRef.current.routeViewsEnabled
    );
  }, [routeViewsVersion, getMapOpsProps]);

  // manipulate route views state on allHeatmapTrips changes
  // (reset to default when new heatmap data loads)
  useEffect(() => {
    // reset showRouteViewsPopup to false
    setShowRouteViewsPopup(false);
    // reset mapRef.current.routeViewsEnabled to default active and rest inactive
    if (mapRef.current) {
      mapRef.current.routeViewsEnabled = {
        DEFAULT: true,
        POINT_TO_POINT: false,
        SNAP_TO_ROAD: false,
        DIRECTIONS_SERVICE: false,
      };
      bumpRouteViewsVersion((x) => x + 1);
    }
  }, [allHeatmapTrips]);
  return (
    <>
      {/* Group Filter Popup */}
      {MemoizedGroupSelector}

      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div
          className={`flex rounded-lg ${userIsImpersonated ? "h-[calc(100vh-7rem-40px)]" : "h-[calc(100vh-7rem)]"} overflow-hidden relative`}
        >
          {!mapStateTransitionInProgress &&
            !isFetchingOrgGroups &&
            !isFetchingOrgGeozones &&
            !isFetchingHeatmapConfig &&
            !isFetchingHeatmapEvents &&
            !!allHeatmapTrips?.length &&
            !!dataHeatmapConfig?.results?.[0]?.legends &&
            mapState?.mapType !== GOOGLE_MAP_MODE_NAMES.STREETVIEW && (
              <div
                className={`absolute top-4 mt-1 left-72 ms-5 flex items-start gap-2 z-[200]`}
                // z-[${APP_CONFIG.MAPS.CONTROL_BUTTONS_ZINDEX}]
              >
                {/* Legends */}
                <div className="bg-white shadow-md rounded-lg flex flex-col">
                  <button
                    type="button"
                    className={`group flex flex-col gap-3 px-3 py-2 rounded-lg${legendExpanded ? " rounded-bl-none rounded-br-none" : ""}`}
                    onClick={() => setLegendExpanded(!legendExpanded)}
                  >
                    <span className="flex items-center justify-between gap-4 text-sidemenu-text-active font-semibold">
                      {t("legends")}
                      <span className="size-7 flex items-center justify-center rounded-lg group-hover:bg-accent-blue-paleO66">
                        <img
                          src={
                            legendExpanded
                              ? svgIcons.MinimizeIcon
                              : svgIcons.ExpandWindowIcon
                          }
                          alt="minimize"
                        />
                      </span>
                    </span>
                  </button>
                  {legendExpanded && (
                    <div className="px-3 pb-3">
                      <hr className="h-1 pb-2" />
                      <div className="flex flex-col gap-2.5 pt-2">
                        {allLegendFilters?.map((legendItem, i) => (
                          <div
                            className="flex items-center gap-4 text-sidemenu-text-active font-semibold pe-8"
                            key={i}
                          >
                            <span
                              className="w-6 h-2 rounded-md"
                              style={{ backgroundColor: legendItem.color }}
                            />
                            {legendItem.label}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Route Style Filter */}
                <div
                  className="relative z-[60]"
                  // z-[APP_CONFIG.MAPS.ROUTE_STYLE_FILTER_BUTTON_ZINDEX]
                >
                  <AdminFormFieldSubmit
                    type="button"
                    variant="secondary"
                    buttonDimensionClass={`${!!showRouteViewsPopup ? "enabled:bg-accent-blue-pale" : "enabled:bg-white"} border-0 h-10 w-10 px-3`}
                    customWrapperClass="shadow-md rounded-lg"
                    label={
                      <span className="flex size-5">
                        <img
                          src={svgIcons.LineStyleIcon}
                          alt={"line-style"}
                          className="flex"
                        />
                      </span>
                    }
                    titleText={t("line_style_filter")}
                    onClick={() => {
                      setShowRouteViewsPopup((state) => !state);
                    }}
                  />
                  {!!showRouteViewsPopup && (
                    <div className="absolute bg-white shadow-md rounded-lg px-3 pt-3 pb-2 flex flex-col gap-3 top-12 left-0 w-56">
                      {RouteCheckboxes}
                    </div>
                  )}
                </div>
              </div>
            )}
          <div
            className={`flex flex-col gap-4 w-72 overflow-auto ${
              // hidden xl:block
              // !allHeatmapTrips ? " opacity-40 pointer-events-none" : ""
              ""
              // disabled diablo temporarily
            } bg-gray-100`}
          >
            <h1 className="sticky top-0 bg-gray-100 px-4 py-4 text-2xl text-heading-black font-bold z-50">
              {t("heading")}
            </h1>
            <div className="px-4">
              <button
                className="w-full flex items-center justify-center disabled:bg-gray-200 enabled:bg-accent-blue-pale enabled:hover:bg-accent-blue-dark enabled:hover:text-white group py-2 px-3 rounded-lg gap-2 enabled:cursor-pointer"
                type="button"
                onClick={() => {
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showVehicleDetails: false,
                      showLayerFilter: false,
                      showVehicleFilter: false,
                      ...(accountHasGroupHierarchyEnabled === "1"
                        ? {
                            showGroupSelector:
                              true /* showHierarchyGroupSelector: true */,
                          }
                        : { showGroupSelector: true }),
                    })
                  );
                }}
                disabled={
                  isFetchingOrgGroups ||
                  isFetchingHeatmapEvents ||
                  replayInProgress ||
                  !dataHeatmapConfig?.results?.[0]?.legends?.length
                }
              >
                <p className="font-medium text-base leading-6 group-disabled:opacity-50">
                  {t("select_groups_assets")}
                </p>
                <img
                  src={svgIcons.GroupFilterIcon}
                  className="group-disabled:opacity-50 group-disabled:grayscale group-enabled:group-hover:grayscale-0 group-enabled:group-hover:brightness-0 group-enabled:group-hover:invert"
                  alt="group-filter-icon"
                />
              </button>
            </div>

            <div className="px-4">
              {/* <pre className="absolute top-0 right-0 text-[8px] z-20">
                {JSON.stringify(legendFilters, null, 2)}
              </pre> */}
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                <pre className="absolute top-0 right-0 text-[8px] max-h-72 overflow-auto z-20">
                  allLegendFilters to TSelectOption[]:
                  <br />
                  {JSON.stringify(allLegendFilters, null, 2)}
                  <br />
                  =====================
                  <br />
                  selectedLegendFilters:
                  <br />
                  {JSON.stringify(selectedLegendFilters, null, 2)}
                </pre>
              )}
              <AdminFormFieldDropdown
                label={t("routes")}
                dimensionClass="w-full min-h-11"
                id="routes"
                name="routes"
                isMulti={true}
                value={selectedLegendFilters as TSelectboxOption[]}
                options={allLegendFilters}
                onChange={(e) => {
                  setSelectedLegendFilters(e as any);
                }}
                error={
                  allLegendFilters.length > 0 &&
                  selectedLegendFilters.length === 0
                    ? "Please select atleast one route filter"
                    : undefined
                }
                touched={true}
                disabled={
                  isFetchingOrgGroups ||
                  isFetchingHeatmapEvents ||
                  replayInProgress ||
                  !dataHeatmapConfig?.results?.[0]?.legends?.length
                }
              />
            </div>
            <div className="px-4">
              {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && !!debugComponent && (
                <pre className="text-xs">
                  filters: {JSON.stringify(eventDataFilters, null, 2)}
                  <br />
                  --------------
                  <br />
                  Now ISO / UTC:
                  <br />
                  {jsTimeToDBTime(new Date().getTime())}
                  <br />
                  --------------
                  <br />
                  {tzForDisplay}:<br />
                  {formattedDateTimeBasedOnTimezone(
                    new Date().getTime(),
                    tzForDateFns,
                    "yyyy-MM-dd HH:mm:ss"
                  )}
                  <br />
                  --------------
                  <br />
                  {tzForDisplay}:<br />
                  {formattedDateTimeBasedOnTimezone(
                    new Date().getTime(),
                    tzForDateFns,
                    "t"
                  )}
                  <br />
                  --------------
                  <br />
                  SPECIFIC VALUE:
                  <br />
                  2025-07-14T16:00
                  <br />
                  {jsTimeToDBTime(
                    fromZonedTime(`2025-07-14T16:00`, tzForDateFns).getTime()
                  )}
                  <br />
                  --------------
                  <br />
                  END_TIMESTAMP VALUE:
                  <br />
                  {formattedDateTimeBasedOnTimezone(
                    dbTimeToJsTime(eventDataFilters.end_timestamp),
                    tzForDateFns,
                    `yyyy-MM-dd'T'HH:mm`
                  )}
                  <br />
                  --------------
                  <br />
                  --------------
                  <br />
                </pre>
              )}
              <div className="col-span-6 relative">
                <TooptipInfoButton
                  buttonClass="absolute right-0 z-10"
                  text={t("date_time_tooltip", {
                    hours: APP_CONFIG.HEATMAP.TIME_DURATION_HOURS,
                  })}
                />
              </div>
              <AdminFormFieldInput
                customWrapperClass="col-span-6 ft-inline"
                label={t(`date_time`)}
                type="datetime-picker"
                id={`date_time`}
                name={`date_time`}
                value={formattedDateTimeBasedOnTimezone(
                  dbTimeToJsTime(eventDataFilters.end_timestamp),
                  tzForDateFns,
                  `yyyy-MM-dd'T'HH:mm`
                )}
                maxTime={new Date()}
                onDatepickerChange={(fieldName: string, value: any) => {
                  const dateValueToSave = jsTimeToDBTime(
                    fromZonedTime(value, tzForDateFns).getTime()
                  );
                  setEventDataFilters((prev) => ({
                    ...prev,
                    end_timestamp: dateValueToSave,
                  }));
                  if (APP_CONFIG.DEBUG.ALL) {
                    console.log("value", value);
                    console.log("dateValueToSave", dateValueToSave);
                    console.log(
                      "DATE",
                      formattedDateTimeBasedOnTimezone(
                        // fromZonedTime(`2025-07-14T16:00`, tzForDateFns).getTime(),
                        dbTimeToJsTime(dateValueToSave),
                        tzForDateFns,
                        "yyyy-MM-dd HH:mm"
                      )
                    );
                  }
                }}
                // onBlur={handleBlur}
                // error={errors.date_time}
                // touched={touched.date_time}
                detailsFormField={true}
                size="lg"
                disabled={
                  isFetchingOrgGroups ||
                  isFetchingHeatmapEvents ||
                  replayInProgress ||
                  !dataHeatmapConfig?.results?.[0]?.legends?.length
                }
                // placement="right"
              />
            </div>
            <div className="flex justify-between px-4 mt-4">
              <p className="font-medium text-lg leading-6">{t("routes")}</p>
              {!!replayPercentage ? (
                <div className="flex w-12 h-4 border border-accent-blue-dark rounded-sm">
                  <div
                    className="bg-accent-blue-dark rounded-sm"
                    style={{ width: `${replayPercentage}%` }}
                  ></div>
                </div>
              ) : (
                <button
                  type="button"
                  className="group flex items-center gap-2 font-medium text-sm disabled:text-heading-gray888/50 enabled:text-accent-blue-dark enabled:hover:text-accent-blue-bright whitespace-nowrap enabled:cursor-pointer"
                  disabled={
                    replayInProgress ||
                    mapStateTransitionInProgress ||
                    isFetchingOrgGroups ||
                    isFetchingOrgGeozones ||
                    isFetchingHeatmapConfig ||
                    isFetchingHeatmapEvents ||
                    !dataHeatmapConfig?.results?.[0]?.legends?.length ||
                    !allHeatmapTrips?.length
                  }
                  onClick={() => {
                    onHeatmapReplayEvents();
                  }}
                >
                  <img
                    src={svgIcons.PlayO}
                    alt=""
                    className="w-[17px] h-[18px] group-disabled:grayscale group-disabled:opacity-40"
                  />
                  {t("view_events_button")}
                </button>
              )}
            </div>

            {/* Route List */}
            <div
              className={`Xflex-grow min-h-48 Xoverflow-auto${replayInProgress ? " opacity-40 pointer-events-none touch-none" : ""}`}
              onMouseOut={() => {
                setHoveredTrip(null);
              }}
            >
              {!!allHeatmapTrips &&
              allHeatmapTrips.length > 0 &&
              !isFetchingHeatmapEvents ? ( // listableDataPoints.length > 0 ? (
                <>
                  {allHeatmapTrips.map((heatmapTrip, heatmapTripIndex) => {
                    const assignedDriver = { contactphone: "" }; // 1234567890
                    const heatmapTripStart = heatmapTrip?.events?.[0];
                    const heatmapTripEnd =
                      heatmapTrip?.events?.[heatmapTrip?.events?.length - 1];
                    const tripStartPoint = {
                      statuscode: VehicleStatusCode.STATUS_IGNITION_ON,
                      address: heatmapTripStart?.address || "",
                      latitude: heatmapTripStart.latitude,
                      longitude: heatmapTripStart.longitude,
                      timestamp: heatmapTripStart.timestamp,
                    } as any as VehicleMapDataPoint;
                    const tripEndPoint = {
                      statuscode: VehicleStatusCode.STATUS_IGNITION_OFF,
                      address: heatmapTripEnd?.address || "",
                      latitude: heatmapTripEnd.latitude,
                      longitude: heatmapTripEnd.longitude,
                      timestamp: heatmapTripEnd.timestamp,
                    } as any as VehicleMapDataPoint;
                    return (
                      <React.Fragment key={heatmapTripIndex}>
                        <Accordian
                          title={
                            <button
                              type="button"
                              className="w-full text-left text-base tracking-normal"
                            >
                              {t("route_n", { n: heatmapTripIndex + 1 })}
                            </button>
                          }
                          openByDefault={heatmapTripIndex === 0}
                          highlightOnHover={true}
                          onMouseOver={() => {
                            setHoveredTrip(heatmapTripIndex);
                          }}
                        >
                          <div className="px-4 pb-4">
                            <div className="flex justify-between pt-2">
                              <div className="space-y-2">
                                <RouteAddress
                                  assetId={consistentDisplayAssetItem(
                                    heatmapTrip.deviceid,
                                    heatmapTrip.description,
                                    heatmapTrip.displayname,
                                    heatmapTrip.drivername,
                                    showDriver
                                  )}
                                  point={tripStartPoint}
                                  pointType="Start"
                                  phone_no={assignedDriver?.contactphone || ""}
                                  address={
                                    tripStartPoint?.address ||
                                    tripStartPoint?.address ||
                                    ""
                                  }
                                  latlng={{
                                    latitude: tripStartPoint?.latitude || 0,
                                    longitude: tripStartPoint?.longitude || 0,
                                  }}
                                  time={formattedDateTimeBasedOnTimezone(
                                    dbTimeToJsTime(
                                      parseInt(`${tripStartPoint?.timestamp}`)
                                    ),
                                    accountData?.timezone ||
                                      APP_CONFIG.DEFAULT_TIMEZONE,
                                    "MM/dd/yyyy (hh:mmaa)"
                                  )}
                                />
                                <RouteAddress
                                  assetId={consistentDisplayAssetItem(
                                    heatmapTrip.deviceid,
                                    heatmapTrip.description,
                                    heatmapTrip.displayname,
                                    heatmapTrip.drivername,
                                    showDriver
                                  )}
                                  point={tripEndPoint}
                                  pointType="End"
                                  phone_no={assignedDriver?.contactphone || ""}
                                  address={
                                    tripEndPoint?.address ||
                                    tripEndPoint?.address ||
                                    ""
                                  }
                                  latlng={{
                                    latitude: tripEndPoint?.latitude || 0,
                                    longitude: tripEndPoint?.longitude || 0,
                                  }}
                                  time={formattedDateTimeBasedOnTimezone(
                                    dbTimeToJsTime(
                                      parseInt(`${tripEndPoint?.timestamp}`)
                                    ),
                                    accountData?.timezone ||
                                      APP_CONFIG.DEFAULT_TIMEZONE,
                                    "MM/dd/yyyy (hh:mmaa)"
                                  )}
                                />
                              </div>
                            </div>
                          </div>
                        </Accordian>
                      </React.Fragment>
                    );
                  })}
                </>
              ) : (
                <div className="flex items-center justify-center h-full relative">
                  {isFetchingHeatmapEvents ||
                  isFetchingOrgGroups /* || isFetchingOrgVehiclesEvents*/ ||
                  !mapState?.mapScriptLoaded /* || !mapState?.pageMapLoaded*/ ? (
                    <>
                      <MapLoadingAnimation
                        bgClassName=""
                        imageClassName="w-12 h-12 mb-12"
                      />
                    </>
                  ) : (
                    <p className="text-gray-400 text-base font-semibold py-24">
                      {t("no_routes_found")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <div
            className="flex-grow relative map-heat-map-wrapper"
            ref={mapWrapperRef}
          >
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <div className="absolute top-0 left-0 px-2 bg-[#FFFFFFAA] text-field-error-dark text-[10px] font-bold uppercase z-10">
                mapType: {mapState?.mapType}
              </div>
            )}
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <pre className="absolute top-56 left-5 px-2 bg-[#FFFFFFAA] p-2 mt-1 text-[10px] z-map-popup Xpointer-events-none">
                allGroupsSelected: {JSON.stringify(allGroupsSelected)}
                <br />
                ---------------------------------
                <br />
                allHeatmapTrips.results (count): {allHeatmapTrips?.length}
                <br />
                isFetchingOrgGeozones:{" "}
                <span
                  className={
                    isFetchingOrgGeozones === true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {JSON.stringify(isFetchingOrgGeozones)}
                </span>
                <br />
                isFetchingHeatmapEvents:{" "}
                <span
                  className={
                    isFetchingHeatmapEvents === true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {JSON.stringify(isFetchingHeatmapEvents)}
                </span>
                <br />
                {/* isFetchingOrgVehiclesEvents: <span className={isFetchingOrgVehiclesEvents === true ? 'text-field-error-dark' : ''}>{JSON.stringify(isFetchingOrgVehiclesEvents)}</span><br /> */}
                mapState.mapScriptLoaded:{" "}
                <span
                  className={
                    mapState?.mapScriptLoaded !== true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {JSON.stringify(mapState?.mapScriptLoaded)}
                </span>
                <br />
                mapState.pageMapLoaded:{" "}
                <span
                  className={
                    mapState?.pageMapLoaded !== true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {JSON.stringify(mapState?.pageMapLoaded)}
                </span>{" "}
                (ignored)
                <br />
                mapState.mapData.ready:{" "}
                <span
                  className={
                    mapState?.mapData?.ready !== true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {JSON.stringify(mapState?.mapData?.ready)}
                </span>
                <br />
                ---------------------------------
                <br />
                FETCHING MAP DATA:
                <br />
                mapStateTransitionInProgress:{" "}
                {JSON.stringify(mapStateTransitionInProgress)}
                {/* <br />
                vehiclesBatch.current: {vehiclesBatch.current}
                <br />
                ---------------------------------
                <br />
                selectedVehicle: {selectedVehicle}
                <br />
                checkedVehicles (count): {checkedVehicles.length} */}
                <br />
                vehicleListCurrentPage: {vehicleListCurrentPage}
                <br />
                ---------------------------------
                <br />
                eventDataFilters:
                <br />
                {JSON.stringify(eventDataFilters, null, 2)}
                <br />
                ---------------------------------
                <br />
                showAddressSearchbox: {JSON.stringify(showAddressSearchbox)}
                <br />
                ---------------------------------
                <br />
                filteredGroupData count:{" "}
                {JSON.stringify(
                  filteredGroupData.filter((item) => !!item.checked).length,
                  null,
                  2
                )}
                <br />
                filteredGroupData: <br />
                <span className="block h-36 overflow-auto">
                  {JSON.stringify(filteredGroupData, null, 2)}
                </span>
              </pre>
            )}
            {!isFetchingOrgGeozones &&
            !!allHeatmapTrips?.length &&
            !!dataHeatmapEvents &&
            !!Object.keys(dataHeatmapEvents)?.length &&
            allHeatmapTrips.length >= allHeatmapTrips?.length && // load map only when all vehicles are loaded (prevent repeated loading / blinking of map)
            !isFetchingHeatmapEvents &&
            !mapStateTransitionInProgress ? (
              <>
                <BasicMap
                  className={`bg-gray-200 h-full w-full${mapSearchboxOpen ? " map-searchbox-open" : ""}`}
                  mapRef={mapRef}
                  mapData={mapState?.mapData}
                  // setMapData={() => {}}
                  nativeMapControls
                  onMapReady={handleMapReady}
                  enableWeather
                />
                <MapTopRightControls
                  mapRef={mapRef as any} // React.MutableRefObject<TMapRef>
                  onGAutocompleteClearCallback={() => {
                    console.log("[onGAutocompleteClearCallback]");
                    // reset map to default view
                    mapUpdatesHandler(
                      getMapOpsProps(),
                      "centerToPushpin",
                      allHeatmapTrips
                    );
                  }}
                  onDirectionChange={handleDirectionsChange}
                  onSearchboxToggle={setMapSearchboxOpen}
                />
                {/* Directions Panel */}
                <DirectionsPanel
                  directionsRenderer={directionsRenderer}
                  onClose={() => setDirectionsRenderer(null)}
                />
              </>
            ) : (
              <div className="h-full w-full flex items-center justify-center relative">
                {typeof allHeatmapTrips?.length !== "undefined" &&
                allHeatmapTrips?.length > 0 ? (
                  <MapLoadingAnimation
                    // bgClassName="bg-red-200"
                    bgOpacityClassName="bg-opacity-100"
                  />
                ) : (
                  <p className="absolute w-full h-full flex items-center justify-center bg-gray-50 text-gray-400 text-lg font-bold">
                    {!mapState?.mapScriptLoaded || isFetchingHeatmapEvents
                      ? tMain("maps.receiving_data")
                      : t(
                          dataHeatmapConfig?.results?.length === 0
                            ? "no_config_set"
                            : "no_vehicles_found"
                        )}
                  </p>
                )}
                {APP_CONFIG.DEBUG.MAPS && (
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 mt-8">
                    <h4 className="font-bold text-lg text-black">
                      {!mapState?.mapData ||
                      Object.keys(mapState?.mapData).length < 2
                        ? tMain("maps.receiving_data")
                        : ""}
                    </h4>
                  </div>
                )}
              </div>
            )}
            <ModalLayerFilters
              onMapTypeChange={handleMapTypeChange}
              onMapLayerChange={handleMapLayerChange}
              onMapCloseClick={handleModalCloseClick}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ScreenMapHeat;
